import * as React from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { DataGrid, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

// import {
//   DataGrid,
//   GridActionsCellItem,
//   GridToolbarContainer,
//   GridToolbarExport,
// } from "@mui/x-data-grid";
import { Backspace, BadgeTwoTone, DeleteForever, DeleteTwoTone } from '@mui/icons-material';
// import { Importer, ImporterField } from 'react-csv-importer';
// import SendEmail from '../Components/SendEmail';
import CertificateIcon from '@mui/icons-material/WorkspacePremium';
import AnimateButton from 'ui-component/extended/AnimateButton';

import SwipeableViews from 'react-swipeable-views';
import { useTheme } from '@mui/material/styles';

import PropTypes from 'prop-types';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import SendEmail from 'Components/SendEmail';
import { visibleFormFields, formatGridDate } from 'utils/formFields';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        'aria-controls': `full-width-tabpanel-${index}`,
    };
}
export default function VerificationPage() {
    const theme = useTheme();
    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };
    const { eventId, role } = useParams();
    const [search, setSearch] = React.useState('');
    const { data, loading, error } = useQuery(GET_ME);
    const [tags, setTags] = React.useState([]);
    const { data: featureData, loading: featureLoading } = useQuery(GET_VERIFICATION_FEATURE, {
        variables: { eventId, role },
    });
    React.useEffect(() => {
        const raw = featureData?.mailVerificationFeature;
        if (!raw) return;
        // {eventId}_forms.features is stored in BOTH shapes across roles of the same event: a JSON
        // array ('["emailVerification"]') and a bare comma list ('PaymentEnable,PromoCode').
        // Parsing only the first left `tags` empty for every comma-list role — so reviewMode never
        // turned on for delegate, and the Pending tab kept reading the live table instead of the
        // review queue.
        const text = String(raw).trim();
        let parsed = [];
        if (text.startsWith('[')) {
            try {
                const json = JSON.parse(text);
                if (Array.isArray(json)) parsed = json.map(String);
            } catch (err) {
                console.error('Could not parse role features as JSON:', err);
            }
        } else {
            parsed = text.split(',').map((t) => t.trim()).filter(Boolean);
        }
        setTags(parsed);
    }, [featureData]);
    if (featureLoading) return <LinearProgress />;

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography>{error.message}</Typography>;
    }

    return (
        <Stack spacing={2}>
            <MainCard>
                <Grid spacing={gridSpacing} container={true}>
                    <Grid item xs={12}>
                        <Stack
                            direction={'row'}
                            justifyContent="space-between"
                            sx={{ bgcolor: 'background.paper', width: '100%', mb: 2 }}
                        >
                            <Typography variant="h1" textAlign={'center'}>
                                Verify {role} Data
                            </Typography>
                            {data?.me?.permissions?.includes('find-' + role) && (
                                <SearchBox onSearch={setSearch} />
                            )}
                        </Stack>

                        <Box sx={{ bgcolor: 'background.paper', width: '100%' }}>
                            <AppBar position="relative" color="secondary">
                                <Tabs
                                    sx={{
                                        '& .Mui-selected': {
                                            background: '#05344f',
                                            color: 'white',
                                        },
                                        background: 'white',
                                        color: 'black',
                                    }}
                                    value={value}
                                    onChange={handleChange}
                                    indicatorColor="secondary"
                                    textColor="inherit"
                                    variant="fullWidth"
                                    aria-label="full width tabs example"
                                >
                                    <Tab
                                        sx={{
                                            '& .Mui-selected': { background: '#05344f' },
                                            border: '1px solid black',
                                        }}
                                        label="Pending Registration"
                                        {...a11yProps(0)}
                                    />
                                    <Tab
                                        sx={{
                                            '& .Mui-selected': { background: '#05344f' },
                                            border: '1px solid black',
                                        }}
                                        label="Accepted Registration"
                                        {...a11yProps(1)}
                                    />
                                    <Tab
                                        sx={{
                                            '& .Mui-selected': { background: '#05344f' },
                                            border: '1px solid black',
                                        }}
                                        label="Rejected Registration"
                                        {...a11yProps(2)}
                                    />
                                </Tabs>
                            </AppBar>
                            <SwipeableViews
                                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                                index={value}
                                onChangeIndex={handleChangeIndex}
                            >
                                <TabPanel value={value} index={0} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status="pending"
                                        permissions={data.me?.permissions?.split(',') || []}
                                    />
                                </TabPanel>
                                <TabPanel value={value} index={1} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status="accept"
                                        permissions={data.me?.permissions?.split(',') || []}
                                    />
                                </TabPanel>
                                <TabPanel value={value} index={2} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status="reject"
                                        permissions={data.me?.permissions?.split(',') || []}
                                    />
                                </TabPanel>
                            </SwipeableViews>
                        </Box>
                    </Grid>
                </Grid>
            </MainCard>
        </Stack>
    );
}

function SearchBox({ onSearch }) {
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        const t = setTimeout(() => onSearch(search), 500);
        return () => clearTimeout(t);
    }, [search]);
    return (
        <TextField
            sx={{ width: 600 }}
            label="Search here..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
                autoComplete: 'new-password',
                endAdornment: (
                    <InputAdornment position="end">
                        {search && (
                            <IconButton onClick={() => setSearch('')}>
                                <Backspace />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
}

function DataTable({ eventId, role, search = '', permissions, tags, status }) {
    const limit = 100;
    // Sponsor-review roles keep the decision record in a separate queue table, and ALL THREE tabs
    // read it. An accepted row does get copied into the role table (that is what makes it a real
    // registration), but the copy carries no `status` — the role table's own `status` column
    // belongs to email-verification, not to this flow — so the normal query, which filters on
    // status='accept', would never find it. The queue is where "accepted" is actually recorded.
    // Same token (`sponsorReview` on {eventId}_forms.features) the registration form reads, so the
    // two halves can never disagree about which roles are in review mode.
    const reviewMode = tags.some((t) => String(t).toLowerCase() === 'sponsorreview');
    const useQueue = reviewMode;

    const liveQuery = useQuery(GET_VERIFIED_DATA, {
        skip: useQueue,
        variables: {
            eventId,
            role,
            status,
            conditions: {
                limit,
                text: search,
                orderBy: 'created',
                order: 'desc',
            },
        },
    });

    const queueQuery = useQuery(GET_REVIEW_QUEUE, {
        skip: !useQueue,
        variables: {
            eventId,
            role,
            status,
            conditions: { limit, text: search, orderBy: 'created', order: 'desc' },
        },
    });

    // One shape for the rest of the component, whichever source is live. The queue query returns
    // `reviewQueue`; normalise it onto `verifiedApplicants` (the row shape is identical — id,
    // status, created, regLink and a JSON `data` blob) so the grid below needs no special case.
    // `form` always comes from the live query, which supplies the column definitions.
    const source = useQueue ? queueQuery : liveQuery;
    const { loading, error, refetch } = source;
    const data = React.useMemo(() => {
        if (!useQueue) return liveQuery.data;
        const q = queueQuery.data;
        return q
            ? { form: q.form, total: q.total, verifiedApplicants: q.reviewQueue || [] }
            : undefined;
    }, [useQueue, liveQuery.data, queueQuery.data]);

    const { data: seatData, refetch: refetchSeats } = useQuery(GET_REVIEW_SEATS, {
        skip: !reviewMode,
        variables: { eventId, role },
    });
    const seats = seatData?.reviewSeats || null;
    const seatsExhausted = !!seats && seats.remaining < 1;
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [currentAction, setCurrentAction] = React.useState({ id: null, value: null });
    const [modalMessage, setModalMessage] = React.useState('');
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    // The email this decision will send, rendered from the applicant's real row by the same code
    // the send path uses. Fetched when the dialog opens so the sponsor can read it BEFORE
    // committing — the decision mails the attendee, and there is no undo.
    const [fetchPreview, previewState] = useLazyQuery(PREVIEW_STATUS_EMAIL, {
        fetchPolicy: 'network-only',
    });

    const handleAcceptReject = (id, value) => {
        setCurrentAction({ id, value });
        if (value === 'accept') {
            setModalMessage(
                <>
                    Are you sure you wish to <b>Accept</b> this application?
                    <br />
                    <br />
                    An <b>Email</b> will be sent to the attendee upon <b>Acceptance</b>.
                </>
            );
        } else if (value === 'reject') {
            setModalMessage(
                <>
                    Are you sure you wish to <b>Reject</b> this application?
                    <br />
                    <br />
                    An <b>Email</b> will be sent to the attendee upon <b>Rejection</b>.
                </>
            );
        }
        // Fail-open: a preview that errors must never block the decision itself, so the error is
        // shown inside the dialog and the Confirm button stays live.
        fetchPreview({ variables: { eventId, role, id, status: value } });
        setIsModalOpen(true);
    };

    const confirmAction = () => {
        setIsLoading(true);

        // In review mode the decision does more than flip a column: accepting MOVES the held row
        // into the role table as a real registration and spends a seat. Different mutation.
        const run = reviewMode
            ? (currentAction.value === 'accept' ? acceptReview : rejectReview)({
                  variables: { eventId, role, id: currentAction.id },
              }).then((r) => {
                  const res = r.data?.acceptReviewApplicant || r.data?.rejectReviewApplicant;
                  // A response with no payload means the server refused; never report that as done.
                  if (!res) {
                      throw new Error(
                          r.errors?.[0]?.message || 'The server did not accept this decision.',
                      );
                  }
                  if (res.seats) refetchSeats();
                  return res.message || 'Done.';
              })
            : updateStatusandSendMail({
                  variables: {
                      eventId,
                      role,
                      id: currentAction.id,
                      field: 'status',
                      value: currentAction.value,
                  },
              }).then((r) => r.data.result);

        run.then((message) => {
            setCurrentAction({ id: null, value: null });
            setModalMessage(message);
            setIsLoading(false);
            setIsModalOpen(true);
        }).catch((e) => {
            setModalMessage(e.message);
            setIsModalOpen(true);
            setIsLoading(false);
        });
    };

    const [updateStatusandSendMail] = useMutation(UPDATE_APPLICANT_STATUS_AND_MAIL, {
        onCompleted: refetch,
        onError: (e) => console.log(e),
    });
    // No onError handler here: Apollo treats a handled error as handled, resolves the promise,
    // and the caller's .catch never fires — a refused decision then fell through to the generic
    // "Done." message. Let it reject so confirmAction can show the real reason.
    const [acceptReview] = useMutation(ACCEPT_REVIEW_APPLICANT, { onCompleted: refetch });
    const [rejectReview] = useMutation(REJECT_REVIEW_APPLICANT, { onCompleted: refetch });
    const [updateField] = useMutation(UPDATE_APPLICANT_FIELD, {
        onCompleted: refetch,
        onError: (e) => console.log(e),
    });
    const [doDelete] = useMutation(DELETE_APPLICANT, { onCompleted: refetch });

    // console.log(data?.verifiedApplicants);

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography color="error">{error.message}</Typography>;
    }
    return (
        <Stack spacing={2}>
            <Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <AnimateButton>
                        <Button variant="contained" disabled={loading} onClick={() => refetch()}>
                            Refresh
                        </Button>
                    </AnimateButton>
                    {/* Seats are spent by accepting, so the sponsor needs the count in front of
                        them while deciding — not as an error after they have already clicked. */}
                    {reviewMode && seats && (
                        <Typography
                            variant="body2"
                            color={seatsExhausted ? 'error.main' : 'text.secondary'}
                        >
                            {seatsExhausted
                                ? `All ${seats.allowed} seats used — contact the organiser for more.`
                                : `${seats.used} of ${seats.allowed} seats used · ${seats.remaining} remaining`}
                        </Typography>
                    )}
                    {permissions.includes('admin') && permissions.includes('bulk-email') ? (
                        <SendEmail {...{ selectedIds, eventId, role }} />
                    ) : (
                        <div />
                    )}
                </Stack>
            </Box>
            <Box>
                <Dialog
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    aria-labelledby="message-dialog-title"
                    maxWidth={currentAction.id ? 'sm' : false}
                    fullWidth={!!currentAction.id}
                >
                    <DialogTitle id="message-dialog-title" sx={{ fontSize: 24 }}>
                        {currentAction.id ? 'Confirm Action' : 'Message'}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>{modalMessage}</DialogContentText>

                        {/* The actual email this decision sends. Shown only while confirming a
                            decision — the same dialog is reused afterwards for the result message. */}
                        {!!currentAction.id && (
                            <Box sx={{ mt: 2 }}>
                                {previewState.loading && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading email preview…
                                        </Typography>
                                    </Stack>
                                )}

                                {!previewState.loading && previewState.error && (
                                    <Alert severity="info" variant="outlined">
                                        Could not load the email preview. You can still confirm —
                                        the email is unaffected.
                                    </Alert>
                                )}

                                {!previewState.loading &&
                                    !previewState.error &&
                                    previewState.data?.preview && (
                                        <>
                                            {previewState.data.preview.warning && (
                                                <Alert
                                                    severity="warning"
                                                    variant="outlined"
                                                    sx={{ mb: 2 }}
                                                >
                                                    {previewState.data.preview.warning}
                                                </Alert>
                                            )}

                                            {previewState.data.preview.body && (
                                                <Box
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            px: 2,
                                                            py: 1,
                                                            bgcolor: 'action.hover',
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            To: {previewState.data.preview.to || '—'}
                                                        </Typography>
                                                        <Typography variant="subtitle2">
                                                            {previewState.data.preview.subject ||
                                                                '(no subject)'}
                                                        </Typography>
                                                    </Box>
                                                    {/* Organiser-authored template, rendered as the
                                                        attendee will see it. */}
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            maxHeight: 320,
                                                            overflowY: 'auto',
                                                            fontSize: 14,
                                                            '& img': { maxWidth: '100%' },
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: previewState.data.preview.body,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            variant="outlined"
                            color="error"
                        >
                            Close
                        </Button>
                        {!!currentAction.id && (
                            <>
                                <Button onClick={confirmAction} variant="contained" color="success">
                                    {isLoading ? (
                                        <CircularProgress color="primary" size={20} />
                                    ) : (
                                        'Confirm'
                                    )}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>

                <DataGrid
                    sx={{ height: 600 }}
                    paginationMode="server"
                    pageSize={limit}
                    rowsPerPageOptions={[limit]}
                    onPageChange={(p) =>
                        refetch({
                            conditions: {
                                offset: p * limit,
                                limit,
                                orderBy: 'created',
                                order: 'desc',
                            },
                        })
                    }
                    rowCount={data?.total || 0}
                    checkboxSelection={
                        permissions.includes('admin') || permissions.includes('email')
                    }
                    onSelectionModelChange={(ids) => {
                        setSelectedIds(
                            ids.map((id) => {
                                const applicant = JSON.parse(
                                    data?.verifiedApplicants?.find((d) => d.id === id)?.data ||
                                        '{"email":"undefined"}'
                                );
                                return {
                                    id,
                                    email: applicant.email || applicant.Email,
                                };
                            })
                        );
                    }}
                    onCellEditCommit={(p, e) =>
                        e.key === 'Enter' &&
                        updateField({
                            variables: {
                                eventId,
                                role,
                                id: p.id,
                                field: p.field,
                                value: p.value,
                            },
                        })
                            .then((r) => window.alert(r.data.result))
                            .catch((e) => window.alert(e.message))
                    }
                    components={{
                        Toolbar: permissions.includes('exp-' + role) ? GridToolbar : '',
                    }}
                    // componentsProps={{
                    //   toolbar: { printOptions: { disableToolbarButton: true } },
                    // }}
                    columns={[
                        {
                            type: 'actions',
                            field: 'actions',
                            headerName: 'Actions',
                            width: 150,
                            getActions: (p) =>
                                [
                                    permissions.includes('del-' + role) && (
                                        <GridActionsCellItem
                                            label="Delete"
                                            onClick={() => {
                                                if (
                                                    !window.confirm(
                                                        'Are you sure you want to delete this?'
                                                    )
                                                ) {
                                                    return;
                                                }
                                                doDelete({ variables: { eventId, role, id: p.id } })
                                                    .catch((e) => window.alert(e.message))
                                                    .then((r) => window.alert(r.data.result));
                                            }}
                                            icon={<DeleteTwoTone color="error" />}
                                        />
                                    ),
                                    permissions.includes('admin') &&
                                        // Review-mode roles get the same triage controls without
                                        // needing the emailVerification token: the decision is the
                                        // sponsor's, not an email-verification step.
                                        (tags.includes('emailVerification') || reviewMode) && (
                                            <React.Fragment>
                                                {(p.row.status === 'pending' ||
                                                    p.row.status === 'reject') && (
                                                    <GridActionsCellItem
                                                        label="Accept"
                                                        // Out of seats → the accept would be
                                                        // refused by the server anyway; don't
                                                        // invite the click.
                                                        disabled={seatsExhausted}
                                                        onClick={() =>
                                                            handleAcceptReject(p.id, 'accept')
                                                        }
                                                        icon={<ThumbUpOffAltIcon color="success" />}
                                                    />
                                                )}
                                                {/* In review mode an accept is not a status flip —
                                                    the row has MOVED into the role table and is a
                                                    real registration. Rejecting it afterwards
                                                    would leave that registration in place while
                                                    marking the queue row rejected, so the server
                                                    refuses. Don't offer the button. */}
                                                {(p.row.status === 'pending' ||
                                                    (p.row.status === 'accept' && !reviewMode)) && (
                                                    <GridActionsCellItem
                                                        label="Reject"
                                                        onClick={() =>
                                                            handleAcceptReject(p.id, 'reject')
                                                        }
                                                        icon={<ThumbDownOffAltIcon color="error" />}
                                                    />
                                                )}
                                                {p.row.status === 'accept' && (
                                                    <GridActionsCellItem
                                                        label="Badge"
                                                        onClick={() =>
                                                            window.open(
                                                                `https://api-reg.eventegration.com/graphql/badge/${eventId}/${role}/${p.id}`
                                                            )
                                                        }
                                                        icon={<BadgeTwoTone />}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ),
                                ].filter(Boolean),
                        },
                        { field: 'id', width: 150 },

                        {
                            field: 'created',
                            width: 200,
                            valueFormatter: (row) => formatGridDate(row.value, format),
                        },
                        { field: 'regLink', width: 300, headerName: 'Registration Link' },

                        ...visibleFormFields(data?.form?.fields).map((f) => ({
                            field: f.name,
                            width: 200,
                            editable: permissions.includes('edit-' + role),
                        })),
                    ]}
                    rows={data?.verifiedApplicants.map((row) => ({
                        ...row,
                        ...JSON.parse(row.data),
                    }))}
                />
            </Box>
        </Stack>
    );
}

export const GET_VERIFIED_DATA = gql`
    query ($eventId: String!, $role: ROLE!, $status: String, $conditions: InputConditions) {
        verifiedApplicants(
            eventId: $eventId
            role: $role
            status: $status
            conditions: $conditions
        ) {
            serial
            id
            created
            regLink
            data
        }
        total: verifiedApplicantsCount(
            eventId: $eventId
            role: $role
            status: $status
            conditions: $conditions
        )

        form(eventId: $eventId, role: $role) {
            fields {
                name
            }
        }
    }
`;

const UPDATE_APPLICANT_FIELD = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!, $field: String!, $value: String!) {
        result: updateApplicantField(
            eventId: $eventId
            role: $role
            id: $id
            field: $field
            value: $value
        )
    }
`;
const UPDATE_APPLICANT_STATUS_AND_MAIL = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!, $field: String!, $value: String!) {
        result: updateApplicantStatusandMail(
            eventId: $eventId
            role: $role
            id: $id
            field: $field
            value: $value
        )
    }
`;

const DELETE_APPLICANT = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!) {
        result: deleteApplicant(eventId: $eventId, role: $role, id: $id)
    }
`;

const GET_ME = gql`
    query {
        me {
            permissions
        }
    }
`;
export const GET_VERIFICATION_FEATURE = gql`
    query ($eventId: String!, $role: ROLE!) {
        mailVerificationFeature(eventId: $eventId, role: $role)
    }
`;

export const GET_REVIEW_QUEUE = gql`
    query ($eventId: String!, $role: ROLE!, $status: String, $conditions: InputConditions) {
        reviewQueue(eventId: $eventId, role: $role, status: $status, conditions: $conditions) {
            id
            status
            created
            regLink
            data
        }
        total: reviewQueueCount(
            eventId: $eventId
            role: $role
            status: $status
            conditions: $conditions
        )
        # Same column definitions the live query supplies — fetched here too so this query is
        # self-sufficient when the live one is skipped.
        form(eventId: $eventId, role: $role) {
            fields {
                name
            }
        }
    }
`;

export const ACCEPT_REVIEW_APPLICANT = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!) {
        acceptReviewApplicant(eventId: $eventId, role: $role, id: $id) {
            ok
            message
            movedId
            seats {
                allowed
                used
                remaining
            }
        }
    }
`;

export const REJECT_REVIEW_APPLICANT = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!) {
        rejectReviewApplicant(eventId: $eventId, role: $role, id: $id) {
            ok
            message
        }
    }
`;

export const GET_REVIEW_SEATS = gql`
    query ($eventId: String!, $role: ROLE!) {
        reviewSeats(eventId: $eventId, role: $role) {
            allowed
            used
            remaining
        }
    }
`;

export const PREVIEW_STATUS_EMAIL = gql`
    query ($eventId: String!, $role: ROLE!, $id: String!, $status: String!) {
        preview: previewApplicantStatusEmail(
            eventId: $eventId
            role: $role
            id: $id
            status: $status
        ) {
            subject
            body
            to
            warning
        }
    }
`;

/*
const PERMISSIONS = [
imp-visitor,reg-visitor,exp-visitor,del-visitor,edit-visitor,badge-visitor,find-visitor,imp-exhibitor,reg-exhibitor,exp-exhibitor,del-exhibitor,edit-exhibitor,badge-exhibitor,find-exhibitor,imp-organizer,reg-organizer,exp-organizer,del-organizer,edit-organizer,badge-organizer,find-organizer,email,graph,admin
];

*/
